import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, EyeOutlined, ThunderboltOutlined, WarningOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, {
  hrmsKeys,
  type TimesheetEmployeeSummary,
  type TimesheetGenerationInput,
  type TimesheetGenerationResult,
} from '@/services/hrms.service';
import departmentService from '@/services/department.service';
import { useAuthStore } from '@/store/authStore';
import { useEmployeeOptions } from './shared/useEmployeeOptions';

const { Text } = Typography;

const RULES = [
  'Approved leave (including half-day leave) comes first, then public holidays from the holiday calendar, then weekly off days from the location work week (or the company work week policy). Every other day is a working day at the employee\'s shift hours.',
  'Overtime comes only from approved OT requests. Late return after leave (duty resumed after the leave end date) is marked Absent.',
  'Working hours are split across projects by the employee\'s project allocation %.',
  'Days already entered manually or imported are never changed. Only previously auto-generated days are replaced, so you can re-run after approving more leave or OT.',
  'Timesheets that are Submitted, Pending approval or Approved are skipped. Rejected timesheets are regenerated and return to Draft.',
];

const TimesheetAutoGeneratePage: React.FC = () => {
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const canRun = Boolean(user?.is_super_admin) || Boolean(user?.permissions?.includes('hr.attendance.manage'));

  const initialMonth = searchParams.get('month');
  const [month, setMonth] = useState<Dayjs>(initialMonth ? dayjs(`${initialMonth}-01`) : dayjs());
  const [departmentId, setDepartmentId] = useState<string>();
  const [locationId, setLocationId] = useState<string>();
  const [employeeIds, setEmployeeIds] = useState<string[]>(
    searchParams.get('employee') ? [searchParams.get('employee')!] : []
  );
  const [includeFuture, setIncludeFuture] = useState(false);
  const [preview, setPreview] = useState<TimesheetGenerationResult | null>(null);
  const [applied, setApplied] = useState<TimesheetGenerationResult | null>(null);

  const { options: empOptions } = useEmployeeOptions({ employment_status: null });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments', 'selection', 'employees-list'],
    queryFn: () => departmentService.getForSelection(),
    staleTime: 5 * 60 * 1000,
  });
  const { data: locations = [] } = useQuery({
    queryKey: hrmsKeys.locations(),
    queryFn: () => hrmsService.listLocations(),
  });

  const input = useMemo(
    (): Omit<TimesheetGenerationInput, 'dry_run'> => ({
      year: month.year(),
      month: month.month() + 1,
      department_id: departmentId,
      location_id: locationId,
      employee_ids: employeeIds.length ? employeeIds : undefined,
      include_future: includeFuture,
    }),
    [month, departmentId, locationId, employeeIds, includeFuture]
  );
  const inputKey = JSON.stringify(input);
  const [previewKey, setPreviewKey] = useState<string>('');
  const previewIsCurrent = Boolean(preview) && previewKey === inputKey;

  const previewMutation = useMutation({
    mutationFn: () => hrmsService.generateTimesheets({ ...input, dry_run: true }),
    onSuccess: (r) => {
      setPreview(r);
      setPreviewKey(inputKey);
      setApplied(null);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Preview failed'),
  });

  const applyMutation = useMutation({
    mutationFn: () => hrmsService.generateTimesheets({ ...input, dry_run: false }),
    onSuccess: (r) => {
      setApplied(r);
      setPreview(r);
      void queryClient.invalidateQueries({ queryKey: hrmsKeys.attendanceMonths() });
      void queryClient.invalidateQueries({ queryKey: ['hrms', 'attendance-days'] });
      message.success(`Timesheets generated for ${r.totals.generated} employee(s)`);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Generation failed'),
  });

  const confirmApply = () => {
    if (!preview) return;
    modal.confirm({
      title: `Generate ${preview.period} timesheets for ${preview.totals.generated} employee(s)?`,
      content: `Creates ${preview.totals.days_created} day(s) and replaces ${preview.totals.days_updated} previously auto-generated day(s), ${preview.from} to ${preview.through}. ${preview.totals.days_kept} manually entered day(s) are kept.`,
      okText: 'Generate',
      onOk: () => applyMutation.mutateAsync(),
    });
  };

  const result = applied || (previewIsCurrent ? preview : null);

  const columns: ColumnsType<TimesheetEmployeeSummary> = [
    {
      title: 'Employee',
      key: 'employee',
      fixed: 'left',
      width: 200,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.employee_name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.employee_code}
            {r.from && r.to ? ` · ${dayjs(r.from).format('D MMM')}–${dayjs(r.to).format('D MMM')}` : ''}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Result',
      key: 'outcome',
      width: 200,
      render: (_, r) =>
        r.outcome === 'skipped' ? (
          <Tooltip title={r.reason}>
            <Tag color="default">Skipped: {r.reason}</Tag>
          </Tooltip>
        ) : (
          <Space size={4} wrap>
            {r.days_created > 0 && <Tag color="green">{r.days_created} new</Tag>}
            {r.days_updated > 0 && <Tag color="blue">{r.days_updated} replaced</Tag>}
            {r.days_kept > 0 && <Tag color="gold">{r.days_kept} manual kept</Tag>}
            {r.days_removed > 0 && <Tag color="red">{r.days_removed} removed</Tag>}
          </Space>
        ),
    },
    { title: 'Present', dataIndex: 'present', width: 75, align: 'right' },
    { title: 'Half', dataIndex: 'half_day', width: 60, align: 'right' },
    { title: 'Leave', dataIndex: 'leave', width: 65, align: 'right' },
    { title: 'Holiday', dataIndex: 'holiday', width: 75, align: 'right' },
    { title: 'Off', dataIndex: 'weekly_off', width: 55, align: 'right' },
    {
      title: 'Absent',
      dataIndex: 'absent',
      width: 70,
      align: 'right',
      render: (v: number) => (v ? <Text type="danger">{v}</Text> : v),
    },
    { title: 'Hours', dataIndex: 'worked_hours', width: 80, align: 'right' },
    { title: 'OT', dataIndex: 'ot_hours', width: 60, align: 'right' },
    { title: 'Project hrs', dataIndex: 'project_hours', width: 95, align: 'right' },
    {
      title: 'Schedule used',
      key: 'schedule',
      width: 260,
      render: (_, r) =>
        r.outcome === 'skipped' ? (
          '—'
        ) : (
          <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
            <Text style={{ fontSize: 12 }}>Week: {r.work_week}</Text>
            <Text style={{ fontSize: 12 }}>
              Shift: {r.shift} · Holidays: {r.holiday_calendar}
            </Text>
          </Space>
        ),
    },
    {
      title: 'Warnings',
      key: 'warnings',
      width: 90,
      render: (_, r) =>
        r.warnings.length ? (
          <Tooltip
            title={
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {r.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            }
          >
            <Tag color="orange" icon={<WarningOutlined />}>
              {r.warnings.length}
            </Tag>
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Auto-generate monthly timesheets"
          subtitle="Build each employee's month from shifts, work week, holidays, approved leave, approved OT and project allocations"
          primaryActions={
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/attendance?tab=months')}>
              Monthly timesheets
            </Button>
          }
        />
      }
    >
      {!canRun && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="You need the hr.attendance.manage permission to generate timesheets."
        />
      )}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="How the timesheet is built"
        description={
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {RULES.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap align="center">
          <DatePicker
            picker="month"
            value={month}
            onChange={(d) => d && setMonth(d)}
            allowClear={false}
            format="MMM YYYY"
          />
          <Select
            placeholder="All departments"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            options={departments.map((d) => ({ value: d.id, label: d.department_name }))}
            value={departmentId}
            onChange={setDepartmentId}
          />
          <Select
            placeholder="All locations"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
            options={locations.map((l: any) => ({ value: l.id, label: `${l.location_code} — ${l.location_name}` }))}
            value={locationId}
            onChange={setLocationId}
          />
          <Select
            mode="multiple"
            placeholder="All employees"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 360 }}
            maxTagCount="responsive"
            options={empOptions}
            value={employeeIds}
            onChange={setEmployeeIds}
          />
          <Checkbox checked={includeFuture} onChange={(e) => setIncludeFuture(e.target.checked)}>
            Include future days (projected)
          </Checkbox>
          <Button
            icon={<EyeOutlined />}
            loading={previewMutation.isPending}
            disabled={!canRun}
            onClick={() => previewMutation.mutate()}
          >
            Preview
          </Button>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            disabled={!canRun || !previewIsCurrent || !preview?.totals.generated || Boolean(applied)}
            loading={applyMutation.isPending}
            onClick={confirmApply}
          >
            Generate
          </Button>
        </Space>
        {preview && !previewIsCurrent && !applied && (
          <div style={{ marginTop: 8 }}>
            <Text type="warning">Filters changed — preview again before generating.</Text>
          </div>
        )}
      </Card>

      {result ? (
        <>
          {applied && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message={`Generated ${applied.period} timesheets for ${applied.totals.generated} employee(s)`}
              description="Review them on the Monthly timesheets tab, fix individual days on the daily register if needed, then submit for approval."
              action={
                <Button onClick={() => navigate(`/hr/attendance?tab=months&month=${applied.period}`)}>
                  Open monthly timesheets
                </Button>
              }
            />
          )}
          {result.warnings.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Check your setup"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {result.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              }
            />
          )}
          <Card
            size="small"
            style={{ marginBottom: 16 }}
            title={`${result.dry_run ? 'Preview' : 'Result'} — ${dayjs(result.from).format('D MMM')} to ${dayjs(result.through).format('D MMM YYYY')}`}
          >
            <Row gutter={16}>
              <Col xs={12} md={4}>
                <Statistic title="Employees" value={result.totals.employees} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Will generate" value={result.totals.generated} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Skipped" value={result.totals.skipped} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="New days" value={result.totals.days_created} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Replaced days" value={result.totals.days_updated} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Manual days kept" value={result.totals.days_kept} />
              </Col>
            </Row>
          </Card>
          <Table<TimesheetEmployeeSummary>
            rowKey="employee_id"
            size="small"
            columns={columns}
            dataSource={result.employees}
            scroll={{ x: 1500 }}
            pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'] }}
          />
        </>
      ) : (
        <Card>
          <Empty description="Choose the month and click Preview to see what will be generated. Nothing is saved until you click Generate." />
        </Card>
      )}
    </WorkspaceLayout>
  );
};

export default TimesheetAutoGeneratePage;
